/**
 * Proof Library (§D) — typed v2 editor for the three tables §D is scored on
 * (`bb_review_stats`, `bb_credentials` where kind IN (certification, award),
 * `bb_case_studies`) that had zero live editor anywhere in the app. Ports the
 * logic proven out in `brand-bible-wizard/steps/StepProof.tsx` (dead route,
 * left untouched on disk) into a section-local panel on the CompliancePanel/
 * VisualIdentityPanel template — see architect plan
 * `.scratch/panel3-D/architect-plan-section-d.md` for the full design.
 *
 * EXPLICIT SAVE (bb-phase2-explicit-save-brief-20260810): edits no longer
 * reach the wire on blur. They buffer in `proofBuffer` and commit when the
 * user presses the page's "Save D · {n} changes" button, which calls the
 * `save()` registered here through `useRegisterBbSectionSave('D', …)`.
 * `save()` replays the buffer through the SAME store actions this panel
 * always used (`upsertEntry` / `discardEntry`) — the commit path is unchanged,
 * only its timing and ordering are. Three consequences worth knowing:
 *
 *  - Every inline input is CONTROLLED now. Under `defaultValue`, Discard could
 *    clear the buffer while the user's typed text stayed on screen — the
 *    displayed value has to come from the buffer for Discard to mean anything.
 *  - Verify stays IMMEDIATE (an operator act, not an edit) but is DISABLED on
 *    a row with buffered edits. §D is the only section whose verified branch
 *    renders read-only text INSTEAD of the inputs, so verifying a dirty row
 *    would flip the row and strand the unsaved value somewhere unreachable.
 *  - §D claims NO flat brand_bible columns in `lib/bbLegacyFieldOwnership`.
 *    Everything editable here lives in bb_* tables; the two legacy §D
 *    free-text columns are page-level, still autosaved for non-v2 tenants,
 *    and must keep flowing through that path.
 *
 * FIXES A LIVE BUG (4c): §D's old two legacy textareas wrote flat
 * `credentials_licenses`/`case_studies` strings that `LEGACY_ONLY_SCALAR_KEYS`
 * deliberately excludes from the v2-active save path — for a v2-active
 * tenant, typing into those fields persisted nothing. The server-side
 * projection (`projection_map.json`) already composes those same flat
 * columns from these three tables' VERIFIED rows, so editing here is a
 * complete replacement, not a partial one — the writer loses nothing.
 *
 * Direct store access, no hook (Q2 in the brief): there is no chunks-style
 * field↔table mapping to hide — `review_stats`/`credentials`/`case_studies`
 * are first-class `BbTable`s, so a `useProofLibrary` hook would just rename
 * five store actions.
 *
 * Two named gaps this design depends on (verified against
 * `backend/api/services/brand_bible_v2/entries.py`, see the plan §0):
 *  - Gap-1: `approved_for_public` IS patchable via PATCH (only CREATE
 *    excludes it) — so "verify" and "approve for public" are legally two
 *    separate actions here, same split as VisualIdentityPanel/OffersPanel.
 *  - Gap-2: `verify_entry` unconditionally rewrites `approved_for_public` to
 *    whatever `approveForPublic` defaults to. This file NEVER passes a third
 *    argument to `verifyEntry` and never offers a re-verify affordance on an
 *    already-verified row, so that foot-gun stays contained.
 *
 * Gap-4 — deliberately NOT porting `ExplicitNoneButton` into this file: §D's
 * three tables are `class: "claim"` projections composed straight into flat
 * columns the blog writer reads (`credentials_licenses`, `case_studies`). A
 * *verified* "none" sentinel row — safe in §G, where compliance rules are
 * injected as rules, never composed into prose — would project the literal
 * text "none" into those columns. That's a content-corruption bug, not a
 * completeness feature, so it's omitted here on purpose.
 *
 * License/insurance are §A's, not §D's: `CredentialsCard` filters to
 * `kind IN ('certification', 'award')` only (StepProof's exact filter) and
 * never writes `display_required` (license-only, drives a §G auto-derived
 * rule). The DB `section` column on `bb_credentials` is fixed to `'A'` even
 * though the row is scored under both sections — a backend fact, not
 * something this UI fights.
 *
 * Honest-UI note (2026-08-10): `client_permission` and `approved_for_public`
 * both persist correctly — writing them is real. Neither is read anywhere
 * downstream today. `backend/api/services/brand_bible_v2/projection.py`
 * `_source_rows()` (~L89-103) and `_compute()` (~L133-159) select claim rows
 * on `verified_by IS NOT NULL` alone; there is no `client_permission` filter
 * and no `approved_for_public` filter anywhere in that file. So a verified
 * case study with `client_permission: false` still projects into the flat
 * `customer_quotes` column, which the writer quotes verbatim
 * (`template_content_crew/generators/blockquote.py`). What IS real and
 * enforced right here in this panel: it refuses to let you approve an
 * unverified row, or a case study with no recorded permission, for public
 * use. The copy below describes that panel-level refusal and treats the two
 * flags as consent/review records, deliberately avoiding language that
 * promises a pipeline-wide gate this system doesn't deliver.
 *
 * Error handling: Verify (the one immediate mutation left) runs through the
 * `guarded()` wrapper, and `save()` reports through the same `saveError`
 * `role="alert"` banner at the panel top (VisualIdentityPanel's pattern).
 * There is deliberately no bare `void upsertEntry(...)` fire-and-forget
 * anywhere in this file — that silent-void gap is a known defect elsewhere in
 * this codebase (§F), not a pattern to repeat here.
 *
 * `LegacyReference` (§5.7 of the plan): the two legacy flat columns
 * (`credentials_licenses`, `case_studies`) are read-only reference text,
 * shown ONLY when the corresponding row list is empty and the legacy string
 * is non-empty. Once a single row exists, the flat column is just this
 * panel's own projection output re-displayed — clutter, not information — so
 * the block disappears on its own. No "migrate" button: unlike §G's
 * enumerable rules, these are freeform blobs that can't be mechanically
 * split into typed rows.
 */
import { useCallback, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbEnvelope, BbPatchPayload, CredentialKind } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge, StepCard, VerificationRow, inputClass } from '@/components/brand-bible-wizard/primitives';
import {
  useRegisterBbSectionSave,
  type BbSectionSaveRegistration,
  type DirtyFormSaveResult,
} from '@/components/brand-bible/save';
import {
  EMPTY_PROOF_BUFFER,
  bufferedField,
  changeCount,
  coerceProofFields,
  isDirty as bufferIsDirty,
  isRemoved,
  parseRating,
  parseReviewCount,
  pendingCreates,
  rowIsDirty,
  setBufferedField,
  stageCreate,
  stageRemoval,
  todayISODate,
  type ProofBuffer,
  type ProofTable,
  type RawFields,
  type RawValue,
} from './proofBuffer';

export interface ProofLibraryLegacyInput {
  credentialsLicenses?: string;
  caseStudies?: string;
}

export interface ProofLibraryPanelProps {
  canVerify: boolean;
  legacy?: ProofLibraryLegacyInput;
}

/* --------------------------------- helpers -------------------------------- */

/**
 * Everything the three cards need to read and mutate the §D buffer. Passed
 * down explicitly rather than through a context — the panel is the only owner
 * and the tree is three levels deep.
 */
interface ProofEditor {
  buffer: ProofBuffer;
  canVerify: boolean;
  /** Buffer one field edit. `stored` is the row's current server value, so an
   *  edit back to it clears the change instead of recording a no-op. */
  setField: (table: ProofTable, id: string, field: string, next: RawValue, stored: RawValue) => void;
  stageAdd: (table: ProofTable, fields: RawFields) => void;
  stageRemove: (table: ProofTable, id: string) => void;
  /** Immediate — an operator act, not an edit. Locked while the row is dirty. */
  verify: (table: ProofTable, id: string) => void;
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} className="text-muted-foreground hover:text-destructive">
      <X className="size-3" />
    </button>
  );
}

/** Stands in for the status rail on a row that only exists in the buffer.
 *  An unsaved row has no provenance, cannot be verified and cannot be
 *  approved for public use — showing those affordances would be a lie. */
function PendingRowRail() {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2.5">
      <EnvelopeBadge kind="auto">unsaved — added when you save §D</EnvelopeBadge>
    </div>
  );
}

/**
 * Shared per-row status strip — source provenance, verify status, and the
 * public-approval gate. Adapted from VisualIdentityPanel's RecordRail, but
 * per-row (one instance per §D row, not one per singleton record) and
 * permission-aware (the disabled reason is the caller's, since case studies
 * add a second gate — client_permission — that no other §D row type has).
 */
function ProofRowRail({
  entry,
  canVerify,
  verifyLocked,
  onVerify,
  publicChecked,
  publicDisabled,
  publicTitle,
  onPublicChange,
  publicAriaLabel,
}: {
  entry: Pick<BbEnvelope, 'source' | 'verified_by' | 'verified_at'>;
  canVerify: boolean;
  /** The row has buffered edits — verifying now would certify the stored
   *  value and hide the unsaved one behind the verified read-only branch. */
  verifyLocked: boolean;
  onVerify: () => void;
  publicChecked: boolean;
  publicDisabled: boolean;
  publicTitle?: string;
  onPublicChange: (checked: boolean) => void;
  publicAriaLabel: string;
}) {
  const isVerified = Boolean(entry.verified_by);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2.5">
      <EnvelopeBadge kind="source">{entry.source}</EnvelopeBadge>
      {isVerified ? (
        <EnvelopeBadge kind="verified">
          Verified{entry.verified_at ? ` · ${new Date(entry.verified_at).toLocaleDateString()}` : ''}
        </EnvelopeBadge>
      ) : verifyLocked ? (
        <span className="text-[11px] text-muted-foreground" title="Save your edits first — verifying now would record the saved value, not the one on screen">
          Pending verification · save your edits first
        </span>
      ) : (
        <VerificationRow verifiedBy={null} canVerify={canVerify} onVerify={onVerify} />
      )}
      <label
        className={cn(
          'ml-auto flex items-center gap-1.5 text-[11px]',
          publicDisabled ? 'cursor-not-allowed text-muted-foreground opacity-60' : 'text-teal-dark',
        )}
        title={publicDisabled ? publicTitle : undefined}
      >
        <input
          type="checkbox"
          aria-label={publicAriaLabel}
          checked={publicChecked}
          disabled={publicDisabled}
          onChange={(e) => onPublicChange(e.target.checked)}
          className="accent-coral disabled:cursor-not-allowed"
        />
        Approved for public use
      </label>
    </div>
  );
}

/* ------------------------------ Review stats ------------------------------ */

function ReviewStatRow({
  id,
  entry,
  editor,
}: {
  id: string;
  /** `null` for a buffered create — the row has no server-side row yet. */
  entry: BbEntry<'review_stats'> | null;
  editor: ProofEditor;
}) {
  const { buffer } = editor;
  const isVerified = Boolean(entry?.verified_by);
  const dirty = rowIsDirty(buffer, 'review_stats', id);

  const storedCount = entry ? String(entry.review_count) : '';
  const storedRating = entry?.rating != null ? String(entry.rating) : '';
  const storedAsOf = entry?.as_of ?? '';
  const platform = String(bufferedField(buffer, 'review_stats', id, 'platform', entry?.platform ?? ''));

  const field = (name: string, stored: string) =>
    String(bufferedField(buffer, 'review_stats', id, name, stored));
  const onField = (name: string, stored: string) => (next: string) =>
    editor.setField('review_stats', id, name, next, stored);

  return (
    <li className="border-b border-border/60 px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-[110px] text-[13.5px] font-semibold capitalize">{platform}</span>
        {isVerified ? (
          <>
            <span className="font-mono text-sm tabular-nums">
              {entry?.review_count} reviews · {entry?.rating != null ? entry.rating.toFixed(1) : '—'}
            </span>
            <EnvelopeBadge kind="source">as of {entry?.as_of}</EnvelopeBadge>
          </>
        ) : (
          <>
            <input
              className={cn(inputClass, 'max-w-[110px] tabular-nums')}
              value={field('review_count', storedCount)}
              aria-label={`Review count for ${platform}`}
              onChange={(e) => onField('review_count', storedCount)(e.target.value)}
            />
            <input
              className={cn(inputClass, 'max-w-[90px] tabular-nums')}
              value={field('rating', storedRating)}
              aria-label={`Rating for ${platform}`}
              onChange={(e) => onField('rating', storedRating)(e.target.value)}
            />
            <input
              type="date"
              className={cn(inputClass, 'max-w-[150px]')}
              value={field('as_of', storedAsOf)}
              max={todayISODate()}
              aria-label={`As-of date for ${platform}`}
              onChange={(e) => onField('as_of', storedAsOf)(e.target.value)}
            />
          </>
        )}
        <span className="ml-auto">
          <RemoveButton
            onClick={() => editor.stageRemove('review_stats', id)}
            label={`Remove ${platform} review stat`}
          />
        </span>
      </div>
      {entry ? (
        <ProofRowRail
          entry={entry}
          canVerify={editor.canVerify}
          verifyLocked={dirty}
          onVerify={() => editor.verify('review_stats', id)}
          publicChecked={Boolean(bufferedField(buffer, 'review_stats', id, 'approved_for_public', entry.approved_for_public))}
          publicDisabled={!isVerified}
          publicTitle={!isVerified ? 'Verify first to allow public use' : undefined}
          onPublicChange={(checked) =>
            editor.setField('review_stats', id, 'approved_for_public', checked, entry.approved_for_public)
          }
          publicAriaLabel={`Approved for public use — ${platform} review stat`}
        />
      ) : (
        <PendingRowRail />
      )}
    </li>
  );
}

function ReviewStatsCard({ editor }: { editor: ProofEditor }) {
  const reviewStats = useBrandBibleWizardStore(selectEntries('review_stats')).filter((r) => r.deleted_at === null);

  const [platform, setPlatform] = useState('');
  const [count, setCount] = useState('');
  const [rating, setRating] = useState('');
  const [asOf, setAsOf] = useState(() => todayISODate());
  const [formError, setFormError] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmedPlatform = platform.trim();
    if (!trimmedPlatform) return;

    const countResult = parseReviewCount(count);
    if ('error' in countResult) {
      setFormError(countResult.error);
      return;
    }
    const ratingResult = parseRating(rating);
    if ('error' in ratingResult) {
      setFormError(ratingResult.error);
      return;
    }
    if (!asOf) {
      setFormError('Date is required — the as-of date is the proof.');
      return;
    }
    if (asOf > todayISODate()) {
      setFormError('Date cannot be in the future.');
      return;
    }

    setFormError(null);
    editor.stageAdd('review_stats', {
      platform: trimmedPlatform,
      review_count: count,
      rating,
      as_of: asOf,
    });
    setPlatform('');
    setCount('');
    setRating('');
    setAsOf(todayISODate());
  };

  return (
    <StepCard title="Review stats" headerRight={<span className="text-xs text-muted-foreground">dated claims only — the as-of date is the proof</span>}>
      <ul>
        {reviewStats
          .filter((r) => !isRemoved(editor.buffer, 'review_stats', r.id))
          .map((r) => (
            <ReviewStatRow key={r.id} id={r.id} entry={r} editor={editor} />
          ))}
        {pendingCreates(editor.buffer, 'review_stats').map((c) => (
          <ReviewStatRow key={c.tempId} id={c.tempId} entry={null} editor={editor} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 p-4">
        <input
          className={cn(inputClass, 'max-w-[160px]')}
          placeholder="platform (google…)"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
        />
        <input
          className={cn(inputClass, 'max-w-[110px] tabular-nums')}
          placeholder="count"
          value={count}
          onChange={(e) => setCount(e.target.value)}
        />
        <input
          className={cn(inputClass, 'max-w-[90px] tabular-nums')}
          placeholder="rating"
          value={rating}
          onChange={(e) => setRating(e.target.value)}
        />
        <input
          type="date"
          className={cn(inputClass, 'max-w-[150px]')}
          value={asOf}
          max={todayISODate()}
          aria-label="As-of date for new review stat"
          onChange={(e) => setAsOf(e.target.value)}
        />
        <button type="button" onClick={handleAdd} className="flex-none rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover">
          Add stat
        </button>
      </div>
      {formError && (
        <p role="alert" className="px-4 pb-3 text-[11px] text-destructive">
          {formError}
        </p>
      )}
    </StepCard>
  );
}

/* -------------------------------- Credentials ------------------------------ */

const CREDENTIAL_KIND_LABEL: Record<'certification' | 'award', string> = {
  certification: 'Certification',
  award: 'Award',
};

const CREDENTIAL_FIELDS = ['issuer_or_state', 'number', 'expiry', 'proof_ref'] as const;

function CredentialRow({
  id,
  entry,
  editor,
}: {
  id: string;
  entry: BbEntry<'credentials'> | null;
  editor: ProofEditor;
}) {
  const { buffer } = editor;
  const isVerified = Boolean(entry?.verified_by);
  const dirty = rowIsDirty(buffer, 'credentials', id);

  const kind = String(bufferedField(buffer, 'credentials', id, 'kind', entry?.kind ?? ''));
  const name = String(bufferedField(buffer, 'credentials', id, 'name', entry?.name ?? ''));

  const stored = (f: (typeof CREDENTIAL_FIELDS)[number]) => (entry ? entry[f] ?? '' : '');

  return (
    <li className="border-b border-border/60 px-4 py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        <EnvelopeBadge kind="source">{kind}</EnvelopeBadge>
        <div className="flex-1">
          <div className="text-[13.5px] font-medium">{name}</div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            <input
              className={cn(inputClass, 'max-w-[160px] text-[12.5px]')}
              value={String(bufferedField(buffer, 'credentials', id, 'issuer_or_state', stored('issuer_or_state')))}
              placeholder="issuer / state"
              aria-label={`Issuer or state for ${name}`}
              onChange={(e) => editor.setField('credentials', id, 'issuer_or_state', e.target.value, stored('issuer_or_state'))}
            />
            <input
              className={cn(inputClass, 'max-w-[140px] text-[12.5px]')}
              value={String(bufferedField(buffer, 'credentials', id, 'number', stored('number')))}
              placeholder="number"
              aria-label={`Number for ${name}`}
              onChange={(e) => editor.setField('credentials', id, 'number', e.target.value, stored('number'))}
            />
            <input
              type="date"
              className={cn(inputClass, 'max-w-[150px] text-[12.5px]')}
              value={String(bufferedField(buffer, 'credentials', id, 'expiry', stored('expiry')))}
              aria-label={`Expiry for ${name}`}
              onChange={(e) => editor.setField('credentials', id, 'expiry', e.target.value, stored('expiry'))}
            />
            <input
              className={cn(inputClass, 'max-w-[220px] text-[12.5px]')}
              value={String(bufferedField(buffer, 'credentials', id, 'proof_ref', stored('proof_ref')))}
              placeholder="proof reference (URL or pointer)"
              aria-label={`Proof reference for ${name}`}
              onChange={(e) => editor.setField('credentials', id, 'proof_ref', e.target.value, stored('proof_ref'))}
            />
          </div>
        </div>
        <RemoveButton onClick={() => editor.stageRemove('credentials', id)} label={`Remove ${name}`} />
      </div>
      {entry ? (
        <ProofRowRail
          entry={entry}
          canVerify={editor.canVerify}
          verifyLocked={dirty}
          onVerify={() => editor.verify('credentials', id)}
          publicChecked={Boolean(bufferedField(buffer, 'credentials', id, 'approved_for_public', entry.approved_for_public))}
          publicDisabled={!isVerified}
          publicTitle={!isVerified ? 'Verify first to allow public use' : undefined}
          onPublicChange={(checked) =>
            editor.setField('credentials', id, 'approved_for_public', checked, entry.approved_for_public)
          }
          publicAriaLabel={`Approved for public use — ${name}`}
        />
      ) : (
        <PendingRowRail />
      )}
    </li>
  );
}

function CredentialsCard({ editor }: { editor: ProofEditor }) {
  // §A owns license/insurance — this card only ever shows/creates the other two kinds.
  const credentials = useBrandBibleWizardStore(selectEntries('credentials')).filter(
    (c) => c.deleted_at === null && (c.kind === 'certification' || c.kind === 'award'),
  );

  const [newKind, setNewKind] = useState<'certification' | 'award'>('certification');
  const [newName, setNewName] = useState('');

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    const kind: CredentialKind = newKind;
    editor.stageAdd('credentials', { kind, name });
    setNewName('');
  };

  return (
    <StepCard title="Certifications & awards" headerRight={<span className="text-xs text-muted-foreground">licenses &amp; insurance live in §A Identity</span>}>
      <ul>
        {credentials
          .filter((c) => !isRemoved(editor.buffer, 'credentials', c.id))
          .map((c) => (
            <CredentialRow key={c.id} id={c.id} entry={c} editor={editor} />
          ))}
        {pendingCreates(editor.buffer, 'credentials').map((c) => (
          <CredentialRow key={c.tempId} id={c.tempId} entry={null} editor={editor} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 p-4">
        <div className="flex flex-none items-center gap-1 rounded-lg border border-input bg-warm-bg p-1">
          {(['certification', 'award'] as const).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={newKind === k}
              onClick={() => setNewKind(k)}
              className={cn(
                'rounded-[6px] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide',
                newKind === k ? 'bg-coral text-white' : 'text-teal hover:bg-teal-light',
              )}
            >
              {CREDENTIAL_KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <input
          className={inputClass}
          placeholder="e.g. Master Plumber #MP-12345"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
        />
        <button type="button" onClick={handleAdd} className="flex-none rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover">
          Add credential
        </button>
      </div>
    </StepCard>
  );
}

/* ------------------------------- Case studies ------------------------------ */

function CaseStudyRow({
  id,
  entry,
  editor,
}: {
  id: string;
  entry: BbEntry<'case_studies'> | null;
  editor: ProofEditor;
}) {
  const { buffer } = editor;
  const isVerified = Boolean(entry?.verified_by);
  const dirty = rowIsDirty(buffer, 'case_studies', id);

  const jobType = String(bufferedField(buffer, 'case_studies', id, 'job_type', entry?.job_type ?? ''));
  const hasPermission = Boolean(
    bufferedField(buffer, 'case_studies', id, 'client_permission', entry?.client_permission ?? false),
  );
  const isPublic = Boolean(
    bufferedField(buffer, 'case_studies', id, 'approved_for_public', entry?.approved_for_public ?? false),
  );

  const publicDisabled = !isVerified || !hasPermission;
  const publicTitle = !isVerified
    ? 'Verify first to allow public use'
    : !hasPermission
      ? "Record client permission first — this panel won't let you approve it for public use until permission is recorded"
      : undefined;

  // Un-checking permission on a row that is already approved for public must
  // revoke the public approval too. Buffered, both land in ONE patch on save,
  // so the contradictory pair (client_permission=false,
  // approved_for_public=true) can never rest server-side even momentarily —
  // the two ordered PATCHes this used to need are no longer necessary.
  const handlePermissionChange = (checked: boolean) => {
    if (!checked && isPublic) {
      editor.setField('case_studies', id, 'approved_for_public', false, entry?.approved_for_public ?? false);
    }
    editor.setField('case_studies', id, 'client_permission', checked, entry?.client_permission ?? false);
  };

  const stored = (f: 'result' | 'approved_quote' | 'metro') => (entry ? entry[f] ?? '' : '');

  return (
    <li className="border-b border-border/60 px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13.5px] font-semibold">{jobType}</span>
        {hasPermission ? (
          <EnvelopeBadge kind="verified">permission granted</EnvelopeBadge>
        ) : (
          <EnvelopeBadge kind="discovered">no permission — internal only</EnvelopeBadge>
        )}
        <label className="ml-2 flex items-center gap-1.5 text-[11px] text-teal-dark">
          <input
            type="checkbox"
            checked={hasPermission}
            aria-label={`Client permission for ${jobType}`}
            onChange={(e) => handlePermissionChange(e.target.checked)}
            className="accent-coral"
          />
          Client permission
        </label>
        <span className="ml-auto">
          <RemoveButton onClick={() => editor.stageRemove('case_studies', id)} label={`Remove ${jobType} case study`} />
        </span>
      </div>
      <div className="mt-2 flex flex-col gap-2">
        <input
          className={cn(inputClass, 'text-[12.5px]')}
          value={String(bufferedField(buffer, 'case_studies', id, 'result', stored('result')))}
          placeholder="result — the concrete outcome"
          aria-label={`Result for ${jobType}`}
          onChange={(e) => editor.setField('case_studies', id, 'result', e.target.value, stored('result'))}
        />
        <textarea
          className={cn(inputClass, 'min-h-[52px] resize-y text-[12.5px]')}
          value={String(bufferedField(buffer, 'case_studies', id, 'approved_quote', stored('approved_quote')))}
          placeholder="approved quote — exact wording, only if permission is recorded"
          aria-label={`Approved quote for ${jobType}`}
          onChange={(e) => editor.setField('case_studies', id, 'approved_quote', e.target.value, stored('approved_quote'))}
        />
        <input
          className={cn(inputClass, 'max-w-[220px] text-[12.5px]')}
          value={String(bufferedField(buffer, 'case_studies', id, 'metro', stored('metro')))}
          placeholder="metro"
          aria-label={`Metro for ${jobType}`}
          onChange={(e) => editor.setField('case_studies', id, 'metro', e.target.value, stored('metro'))}
        />
      </div>
      {entry ? (
        <ProofRowRail
          entry={entry}
          canVerify={editor.canVerify}
          verifyLocked={dirty}
          onVerify={() => editor.verify('case_studies', id)}
          publicChecked={isPublic}
          publicDisabled={publicDisabled}
          publicTitle={publicTitle}
          onPublicChange={(checked) =>
            editor.setField('case_studies', id, 'approved_for_public', checked, entry.approved_for_public)
          }
          publicAriaLabel={`Approved for public use — ${jobType} case study`}
        />
      ) : (
        <PendingRowRail />
      )}
    </li>
  );
}

function CaseStudiesCard({ editor }: { editor: ProofEditor }) {
  const caseStudies = useBrandBibleWizardStore(selectEntries('case_studies')).filter((c) => c.deleted_at === null);

  const [newJobType, setNewJobType] = useState('');
  const [newPermission, setNewPermission] = useState(false);

  const handleAdd = () => {
    const jobType = newJobType.trim();
    if (!jobType) return;
    editor.stageAdd('case_studies', { job_type: jobType, client_permission: newPermission });
    setNewJobType('');
    setNewPermission(false);
  };

  return (
    <StepCard title="Case studies" headerRight={<span className="text-xs text-muted-foreground">client permission is a consent record, not an enforcement gate</span>}>
      <ul>
        {caseStudies
          .filter((c) => !isRemoved(editor.buffer, 'case_studies', c.id))
          .map((cs) => (
            <CaseStudyRow key={cs.id} id={cs.id} entry={cs} editor={editor} />
          ))}
        {pendingCreates(editor.buffer, 'case_studies').map((c) => (
          <CaseStudyRow key={c.tempId} id={c.tempId} entry={null} editor={editor} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2.5 p-4">
        <input
          className={inputClass}
          placeholder="job type — e.g. what kind of job this story is about"
          value={newJobType}
          onChange={(e) => setNewJobType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
        />
        <label className="flex flex-none items-center gap-1.5 text-[11px] text-teal-dark">
          <input
            type="checkbox"
            checked={newPermission}
            onChange={(e) => setNewPermission(e.target.checked)}
            className="accent-coral"
          />
          Client permission
        </label>
        <button type="button" onClick={handleAdd} className="flex-none rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover">
          Add case study
        </button>
      </div>
    </StepCard>
  );
}

/* ------------------------------ Legacy reference ---------------------------- */

function LegacyReference({
  legacy,
  hasCredentialRows,
  hasCaseStudyRows,
}: {
  legacy?: ProofLibraryLegacyInput;
  hasCredentialRows: boolean;
  hasCaseStudyRows: boolean;
}) {
  if (!legacy) return null;

  const credentialsText = (legacy.credentialsLicenses ?? '').trim();
  const caseStudiesText = (legacy.caseStudies ?? '').trim();
  const showCredentials = !hasCredentialRows && credentialsText.length > 0;
  const showCaseStudies = !hasCaseStudyRows && caseStudiesText.length > 0;

  if (!showCredentials && !showCaseStudies) return null;

  return (
    <StepCard title="Legacy reference" className="bg-teal-light" headerRight={<span className="text-xs text-muted-foreground">read-only</span>}>
      <div className="flex flex-col gap-3.5 p-4 text-muted-foreground">
        <p className="text-[11px]">Previously entered text (reference — re-enter as rows above)</p>
        {showCredentials && (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Credentials &amp; licenses (legacy)</div>
            <p className="whitespace-pre-wrap text-[13.5px]">{legacy.credentialsLicenses}</p>
          </div>
        )}
        {showCaseStudies && (
          <div>
            <div className="mb-1.5 text-xs font-semibold text-teal-dark">Case studies (legacy)</div>
            <p className="whitespace-pre-wrap text-[13.5px]">{legacy.caseStudies}</p>
          </div>
        )}
      </div>
    </StepCard>
  );
}

/* ---------------------------------- main panel --------------------------------- */

/** Null and empty-string both mean "no value" across this seam — a cleared
 *  text field coerces to `undefined` while the stored column reads `null`, and
 *  re-sending that pair every save would be a pointless write. */
const equalish = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

const splitRowKey = (key: string): { table: ProofTable; id: string } => {
  const at = key.indexOf(':');
  return { table: key.slice(0, at) as ProofTable, id: key.slice(at + 1) };
};

const rowLabel = (row: Record<string, unknown> | undefined): string =>
  String(row?.platform ?? row?.name ?? row?.job_type ?? '');

export function ProofLibraryPanel({ canVerify, legacy }: ProofLibraryPanelProps) {
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';

  const credentials = useBrandBibleWizardStore(selectEntries('credentials')).filter(
    (c) => c.deleted_at === null && (c.kind === 'certification' || c.kind === 'award'),
  );
  const caseStudies = useBrandBibleWizardStore(selectEntries('case_studies')).filter((c) => c.deleted_at === null);

  const [buffer, setBuffer] = useState<ProofBuffer>(EMPTY_PROOF_BUFFER);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Verify is the one mutation that still goes straight to the wire, so it
  // keeps the original guarded() wrapper and the shared error banner.
  const guarded = (op: () => Promise<unknown>) => {
    void (async () => {
      try {
        await op();
        setSaveError(null);
      } catch {
        setSaveError("Couldn't save that change — try again.");
      }
    })();
  };

  /** One typed entry point per table — `upsertEntry` is generic over BbTable
   *  and will not accept a union table with a widened payload. */
  const commitRow = useCallback(
    (table: ProofTable, payload: Record<string, unknown>, id?: string) => {
      switch (table) {
        case 'review_stats':
          return upsertEntry('review_stats', payload as BbPatchPayload<'review_stats'>, id, bbTenant);
        case 'credentials':
          return upsertEntry('credentials', payload as BbPatchPayload<'credentials'>, id, bbTenant);
        case 'case_studies':
          return upsertEntry('case_studies', payload as BbPatchPayload<'case_studies'>, id, bbTenant);
      }
    },
    [upsertEntry],
  );

  /**
   * Replay the buffer through the store, in a fixed order: creates, then
   * patches, then removals. Rows created AND removed in the same buffer
   * session were already cancelled by `stageRemoval` and never appear here.
   *
   * Every op that succeeds leaves the buffer; every op that fails stays in
   * it, so a partial failure keeps exactly the unsaved work and the user can
   * press Save again. Store rows are read via `getState()` rather than
   * captured from render, so this callback's identity tracks the buffer alone
   * — a registration that changed on every render would re-register in a loop.
   */
  const save = useCallback(async (): Promise<DirtyFormSaveResult> => {
    const state = useBrandBibleWizardStore.getState();
    const rowsOf = (table: ProofTable) =>
      ((state.entries[table] ?? []) as BbEntry<ProofTable>[]).filter((r) => r.deleted_at === null);

    const invalid: string[] = [];
    const failed: string[] = [];
    let next = buffer;

    for (const create of buffer.creates) {
      const label = rowLabel(create.fields as Record<string, unknown>);
      const { payload, errors } = coerceProofFields(create.table, create.fields, label);
      if (errors.length > 0) {
        invalid.push(...errors);
        continue;
      }
      try {
        await commitRow(create.table, payload);
        next = { ...next, creates: next.creates.filter((c) => c.tempId !== create.tempId) };
      } catch {
        failed.push(label || create.table);
      }
    }

    for (const [key, fields] of Object.entries(buffer.patches)) {
      const { table, id } = splitRowKey(key);
      const row = rowsOf(table).find((r) => r.id === id) as Record<string, unknown> | undefined;
      const dropPatch = () => {
        const patches = { ...next.patches };
        delete patches[key];
        next = { ...next, patches };
      };
      if (!row) {
        // The row is gone (deleted elsewhere, or a reload dropped it). Its
        // buffered edits have nothing to apply to — drop them rather than
        // resurrect the row as a create.
        dropPatch();
        continue;
      }
      const label = rowLabel(row);
      const { payload, errors } = coerceProofFields(table, fields, label);
      if (errors.length > 0) {
        invalid.push(...errors);
        continue;
      }
      // Re-diff against the store as it is NOW, not as it was when the edit
      // was typed — snapshot-then-diff, never blind-send.
      const changed = Object.fromEntries(
        Object.entries(payload).filter(([column, value]) => !equalish(row[column], value)),
      );
      if (Object.keys(changed).length === 0) {
        dropPatch();
        continue;
      }
      try {
        await commitRow(table, changed, id);
        dropPatch();
      } catch {
        failed.push(label || table);
      }
    }

    for (const key of buffer.deletes) {
      const { table, id } = splitRowKey(key);
      try {
        await discardEntry(table, id, bbTenant);
        next = { ...next, deletes: next.deletes.filter((d) => d !== key) };
      } catch {
        failed.push(id);
      }
    }

    setBuffer(next);

    if (invalid.length > 0) {
      const error = invalid.join(' ');
      setSaveError(error);
      return { success: false, error };
    }
    if (failed.length > 0) {
      const error = `Couldn't save ${failed.join(', ')} — your edits are still here, try again.`;
      setSaveError(error);
      return { success: false, error };
    }
    setSaveError(null);
    return { success: true };
  }, [buffer, commitRow, discardEntry]);

  const discard = useCallback(() => {
    setBuffer(EMPTY_PROOF_BUFFER);
    setSaveError(null);
  }, []);

  // The registration object MUST change identity whenever dirty/count change:
  // the registry only notifies its subscribers on register/save/discard, so a
  // registration memoized on [] would leave the footer stuck at "Save D" with
  // a frozen count while the user typed.
  const registration = useMemo<BbSectionSaveRegistration>(
    () => ({
      isDirty: () => bufferIsDirty(buffer),
      save,
      discard,
      changeCount: () => changeCount(buffer),
    }),
    [buffer, save, discard],
  );
  useRegisterBbSectionSave('D', registration);

  const editor = useMemo<ProofEditor>(
    () => ({
      buffer,
      canVerify,
      setField: (table, id, field, nextValue, stored) =>
        setBuffer((b) => setBufferedField(b, table, id, field, nextValue, stored)),
      stageAdd: (table, fields) => setBuffer((b) => stageCreate(b, table, fields)),
      stageRemove: (table, id) => setBuffer((b) => stageRemoval(b, table, id)),
      verify: (table, id) => guarded(() => verifyEntry(table, id, undefined, bbTenant)),
    }),
    // `guarded` is re-created each render but only closes over setSaveError.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buffer, canVerify, verifyEntry],
  );

  const visibleCredentials = credentials.filter((c) => !isRemoved(buffer, 'credentials', c.id));
  const visibleCaseStudies = caseStudies.filter((c) => !isRemoved(buffer, 'case_studies', c.id));

  return (
    <div className="flex flex-col gap-3.5">
      {saveError && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          {saveError}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Approved for public use and client permission are recorded here for review — neither is enforced in generated content yet.
      </p>

      <ReviewStatsCard editor={editor} />
      <CredentialsCard editor={editor} />
      <CaseStudiesCard editor={editor} />

      <LegacyReference
        legacy={legacy}
        hasCredentialRows={visibleCredentials.length + pendingCreates(buffer, 'credentials').length > 0}
        hasCaseStudyRows={visibleCaseStudies.length + pendingCreates(buffer, 'case_studies').length > 0}
      />
    </div>
  );
}
